// src/hooks/useApiQueue.js
import { useRef, useCallback } from "react";

export class QueueClearedError extends Error {
  constructor() {
    super("Queue cleared");
    this.name = "QueueClearedError";
  }
}

export const useApiQueue = (initialConcurrency = 1) => {
  const queueRef       = useRef([]);
  const activeCountRef = useRef(0);
  const concurrencyRef = useRef(initialConcurrency);
  const generationRef  = useRef(0); // ✅ har clearQueue pe increment hoga

  const _process = useCallback(() => {
    const currentGen = generationRef.current; // task start hone ka waqt capture

    while (
      activeCountRef.current < concurrencyRef.current &&
      queueRef.current.length > 0
    ) {
      const { task, resolve, reject } = queueRef.current.shift();
      activeCountRef.current++;

      task()
        .then((result) => {
          // ✅ Agar generation change ho gayi (contract badla), response discard karo
          if (generationRef.current !== currentGen) return;
          resolve(result);
        })
        .catch((err) => {
          if (generationRef.current !== currentGen) return;
          reject(err);
        })
        .finally(() => {
          if (generationRef.current === currentGen) {
            activeCountRef.current--;
          }
          _process();
        });
    }
  }, []);

  const enqueue = useCallback(
    (apiFn) =>
      new Promise((resolve, reject) => {
        // ✅ safeReject: QueueClearedError ko silently swallow karo
        // Kisi bhi component mein catch block likhne ki zaroorat nahi
        const safeReject = (err) => {
          if (err instanceof QueueClearedError) return;
          reject(err);
        };
        queueRef.current.push({ task: apiFn, resolve, reject: safeReject });
        _process();
      }),
    [_process]
  );

  const enqueueAll = useCallback(
    (apiFns) => Promise.all(apiFns.map((fn) => enqueue(fn))),
    [enqueue]
  );

  const clearQueue = useCallback(() => {
    generationRef.current++;          // active tasks stale ho jayein
    activeCountRef.current = 0;       // naye tasks immediately shuru ho sakein
    const pending = queueRef.current.splice(0);
    pending.forEach(({ reject }) => reject(new QueueClearedError()));
  }, []);

  const setConcurrency = useCallback(
    (val) => {
      concurrencyRef.current = Math.max(1, val);
      _process();
    },
    [_process]
  );

  return { enqueue, enqueueAll, clearQueue, setConcurrency };
};